import { useEffect, useMemo, useState } from 'react';
import { Trash } from 'phosphor-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  Supplier,
  getSupplierDerivedEmailDomains,
  isFreeEmailDomain,
  normalizeEmailDomain,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { updateSupplier } from '../../../../../redux/services/suppliersService';
import { getUserDetails } from '../../../../../redux/services/authService';
import { getIntegrationName, openDillToast } from '../../../../../utils/helpers';
import { DillAvatar, DillButton, DillInput, DillTag, DillToggle } from '../../../../general/Dill';

type CustomerOnApprovalAction = 'none' | 'create' | 'update';

const SectionCard = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-4">{children}</div>
);

type EmailListField = 'creditApplicationEmails' | 'internalEmails';

// One label + description + toggle line, used by the Sales Rep / approval recipient settings.
const SettingToggleRow = ({
  label,
  description,
  value,
  onChange,
  canEdit,
}: {
  label: string;
  description: string;
  value: boolean;
  onChange: (value: boolean) => void;
  canEdit: boolean;
}) => (
  <div className="flex items-center justify-between gap-4 px-4 py-3">
    <div className="min-w-0">
      <p className="text-xs font-semibold text-gray-800 m-0">{label}</p>
      <p className="text-[11px] text-gray-500 mt-0.5 m-0">{description}</p>
    </div>
    <div
      className="shrink-0"
      data-tooltip-id={!canEdit ? 'no-permission-tooltip-toggle' : undefined}>
      <DillToggle value={value} onChange={onChange} isDisabled={!canEdit} />
    </div>
  </div>
);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailListEditor = ({
  supplier,
  canEdit,
  field,
  title,
  description,
}: {
  supplier?: Supplier | null;
  canEdit: boolean;
  field: EmailListField;
  title: string;
  description: string;
}) => {
  const dispatch = useAppDispatch();
  const { supplierUsers } = useAppSelector((state) => state.supplier);

  const emails = useMemo(
    () => supplier?.creditApplicationSetting?.[field] ?? [],
    [supplier, field]
  );
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  const saveEmails = (next: string[]) =>
    dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: {
          creditApplicationSetting: {
            ...supplier?.creditApplicationSetting,
            [field]: next,
          },
        },
      })
    );

  const handleAddEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Enter a valid email address');
      return;
    }
    if (emails.some((e) => e.toLowerCase() === email)) {
      setEmailError(`${email} is already on the list`);
      return;
    }
    setAdding(true);
    const result = await saveEmails([...emails, email]);
    setAdding(false);
    if (result.type.includes('fulfilled')) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Email added.' });
      setNewEmail('');
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to add email.' });
    }
  };

  const handleRemoveEmail = async (emailToRemove: string) => {
    setRemovingEmail(emailToRemove);
    const result = await saveEmails(
      emails.filter((e) => e.toLowerCase() !== emailToRemove.toLowerCase())
    );
    setRemovingEmail(null);
    if (result.type.includes('fulfilled')) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Email removed.' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to remove email.' });
    }
  };

  const getDisplayName = (email: string) => {
    const u = supplierUsers.find((usr) => usr.email?.toLowerCase() === email.toLowerCase());
    if (!u) return null;
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    return { name: name || email, initials: `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}` };
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-xs font-semibold text-gray-700 m-0">{title}</p>
        <p className="text-[11px] text-gray-400 mt-0.5 m-0">{description}</p>
      </div>

      <div
        className="flex items-start gap-2"
        data-tooltip-id={!canEdit ? 'no-permission-tooltip-emails' : undefined}>
        <div className="flex-1 flex flex-col gap-1">
          <DillInput
            value={newEmail}
            onChange={(v) => {
              setNewEmail(v);
              setEmailError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddEmail();
              }
            }}
            placeholder="name@company.com"
            error={emailError}
            disabled={!canEdit}
          />
          {emailError && <span className="text-[11px] text-red-500">{emailError}</span>}
        </div>
        <DillButton
          label="Add"
          onClick={handleAddEmail}
          isLoading={adding}
          isDisabled={!canEdit || !newEmail.trim() || adding}
        />
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        {emails.length === 0 ? (
          <div className="px-4 py-4 text-center">
            <p className="text-xs text-gray-400 m-0">No emails configured.</p>
          </div>
        ) : (
          emails.map((email, i) => {
            const display = getDisplayName(email);
            const isRemoving = removingEmail?.toLowerCase() === email.toLowerCase();
            return (
              <div
                key={email}
                className={`flex items-center justify-between px-4 py-2.5 bg-white hover:bg-gray-50 transition-colors ${
                  i < emails.length - 1 ? 'border-b border-gray-100' : ''
                }`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <DillAvatar initials={display?.initials || email[0].toUpperCase()} size={28} />
                  <div className="min-w-0">
                    {display?.name && display.name !== email && (
                      <p className="text-xs font-semibold text-gray-800 m-0 truncate">
                        {display.name}
                      </p>
                    )}
                    <p className="text-[11px] text-gray-400 m-0 truncate">{email}</p>
                  </div>
                </div>
                <div
                  className="shrink-0 ml-3"
                  data-tooltip-id={!canEdit ? 'no-permission-tooltip-emails' : undefined}>
                  <DillButton
                    variant="icon-red"
                    icon={<Trash size={14} />}
                    onClick={() => handleRemoveEmail(email)}
                    isLoading={isRemoving}
                    isDisabled={!canEdit || isRemoving}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const BlockedTradeReferenceDomainsCard = ({
  supplier,
  canEdit,
}: {
  supplier?: Supplier | null;
  canEdit: boolean;
}) => {
  const dispatch = useAppDispatch();

  const derivedDomains = useMemo(() => getSupplierDerivedEmailDomains(supplier), [supplier]);
  const manualDomains = useMemo(
    () => supplier?.creditApplicationSetting?.blockedTradeReferenceDomains ?? [],
    [supplier]
  );
  const [newDomain, setNewDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingDomain, setRemovingDomain] = useState<string | null>(null);

  const saveDomains = (next: string[]) =>
    dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: {
          creditApplicationSetting: {
            ...supplier?.creditApplicationSetting,
            blockedTradeReferenceDomains: next,
          },
        },
      })
    );

  const handleAddDomain = async () => {
    const domain = normalizeEmailDomain(newDomain);
    if (!domain) {
      setDomainError('Enter a valid domain, e.g. sistercompany.com');
      return;
    }
    if (isFreeEmailDomain(domain)) {
      setDomainError(`${domain} is a public email provider and can't be blocked`);
      return;
    }
    if (derivedDomains.includes(domain)) {
      setDomainError(`${domain} is already blocked automatically`);
      return;
    }
    if (manualDomains.some((d) => d.toLowerCase() === domain)) {
      setDomainError(`${domain} is already on the list`);
      return;
    }
    setAdding(true);
    const result = await saveDomains([...manualDomains, domain]);
    setAdding(false);
    if (result.type.includes('fulfilled')) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Domain added.' });
      setNewDomain('');
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to add domain.' });
    }
  };

  const handleRemoveDomain = async (domainToRemove: string) => {
    setRemovingDomain(domainToRemove);
    const result = await saveDomains(manualDomains.filter((d) => d !== domainToRemove));
    setRemovingDomain(null);
    if (result.type.includes('fulfilled')) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Domain removed.' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to remove domain.' });
    }
  };

  return (
    <SectionCard>
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900 m-0">Blocked Trade Reference Domains</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Trade references can&apos;t use an email at these domains (subdomains included). Your
            own domains are blocked automatically — add others, such as sister companies.
          </p>
        </div>

        <div
          className="flex items-start gap-2"
          data-tooltip-id={!canEdit ? 'no-permission-tooltip-blocked-domains' : undefined}>
          <div className="flex-1 flex flex-col gap-1">
            <DillInput
              value={newDomain}
              onChange={(v) => {
                setNewDomain(v);
                setDomainError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddDomain();
                }
              }}
              placeholder="e.g. sistercompany.com"
              error={domainError}
              disabled={!canEdit}
            />
            {domainError && <span className="text-[11px] text-red-500">{domainError}</span>}
          </div>
          <DillButton
            label="Add"
            onClick={handleAddDomain}
            isLoading={adding}
            isDisabled={!canEdit || !newDomain.trim() || adding}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold text-gray-700 m-0">Added manually</p>
            {manualDomains.length === 0 ? (
              <p className="text-[11px] text-gray-400 m-0">No domains added.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {manualDomains.map((domain) => (
                  <DillTag
                    key={domain}
                    label={domain}
                    onRemove={canEdit ? () => handleRemoveDomain(domain) : undefined}
                    isLoading={removingDomain === domain}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 border-t border-gray-200 pt-5 md:border-t-0 md:pt-0 md:border-l md:pl-5">
            <p className="text-xs font-semibold text-gray-700 m-0">Blocked automatically</p>
            <p className="text-[11px] text-gray-400 m-0">
              Taken from your account, admin and credit application emails.
            </p>
            {derivedDomains.length === 0 ? (
              <p className="text-[11px] text-gray-400 m-0">None found.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {derivedDomains.map((domain) => (
                  <DillTag key={domain} label={domain} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip-blocked-domains"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </SectionCard>
  );
};

export function CreditApplicationSettingsSection({ supplier }: { supplier?: Supplier | null }) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  // ── filterEachReviewer ──────────────────────────────────────────────────────
  const [filterEachReviewer, setFilterEachReviewer] = useState(
    supplier?.creditApplicationSetting?.filterEachReviewer ?? false
  );
  const [notifySalesRepOnApproval, setNotifySalesRepOnApproval] = useState(
    supplier?.creditApplicationSetting?.notifySalesRepOnApproval ?? true
  );
  const [notifySignersOnApproval, setNotifySignersOnApproval] = useState(
    supplier?.creditApplicationSetting?.notifyInternalSignatureSignersOnApproval ?? false
  );
  const [savingToggle, setSavingToggle] = useState(false);

  useEffect(() => {
    setFilterEachReviewer(supplier?.creditApplicationSetting?.filterEachReviewer ?? false);
    setNotifySalesRepOnApproval(
      supplier?.creditApplicationSetting?.notifySalesRepOnApproval ?? true
    );
    setNotifySignersOnApproval(
      supplier?.creditApplicationSetting?.notifyInternalSignatureSignersOnApproval ?? false
    );
  }, [supplier]);

  const toggleDirty =
    filterEachReviewer !== (supplier?.creditApplicationSetting?.filterEachReviewer ?? false) ||
    notifySalesRepOnApproval !==
      (supplier?.creditApplicationSetting?.notifySalesRepOnApproval ?? true) ||
    notifySignersOnApproval !==
      (supplier?.creditApplicationSetting?.notifyInternalSignatureSignersOnApproval ?? false);

  const saveToggle = async () => {
    setSavingToggle(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: {
          creditApplicationSetting: {
            ...supplier?.creditApplicationSetting,
            filterEachReviewer,
            notifySalesRepOnApproval,
            notifyInternalSignatureSignersOnApproval: notifySignersOnApproval,
          },
        },
      })
    );
    setSavingToggle(false);
    if (result.type.includes('fulfilled')) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Setting saved.' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save setting.' });
    }
  };

  // ── stale ball-in-court reminders ───────────────────────────────────────────
  const savedIsStaleReminderEnabled =
    !!supplier?.creditApplicationSetting?.isStaleBallInCourtReminderEnabled;
  const [isStaleReminderEnabled, setIsStaleReminderEnabled] = useState(savedIsStaleReminderEnabled);
  const [savingStaleReminder, setSavingStaleReminder] = useState(false);

  useEffect(() => {
    setIsStaleReminderEnabled(savedIsStaleReminderEnabled);
  }, [savedIsStaleReminderEnabled]);

  const staleReminderDirty = isStaleReminderEnabled !== savedIsStaleReminderEnabled;

  const saveStaleReminder = async () => {
    setSavingStaleReminder(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: {
          creditApplicationSetting: {
            ...supplier?.creditApplicationSetting,
            isStaleBallInCourtReminderEnabled: isStaleReminderEnabled,
          },
        },
      })
    );
    setSavingStaleReminder(false);
    if (result.type.includes('fulfilled')) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Setting saved.' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save setting.' });
    }
  };

  // ── customerOnApprovalAction ────────────────────────────────────────────────
  const isERPCreditSetup = useMemo(
    () =>
      supplier?.integrationType === 'QBO' ||
      supplier?.integrationType === 'P21' ||
      supplier?.integrationType === 'AGILITY_DMSI' ||
      supplier?.integrationType === 'INFOR_DIST_SX' ||
      supplier?.integrationType === 'ECLIPSE',
    [supplier]
  );

  const [customerOnApprovalAction, setCustomerOnApprovalAction] =
    useState<CustomerOnApprovalAction>(
      (supplier?.creditApplicationSetting?.customerOnApprovalAction as CustomerOnApprovalAction) ??
        'none'
    );
  const [savingIntegrationAction, setSavingIntegrationAction] = useState(false);

  const isP21 = useMemo(() => supplier?.integrationType === 'P21', [supplier]);
  const isEclipse = useMemo(() => supplier?.integrationType === 'ECLIPSE', [supplier]);
  const [
    createErpCustomerOnInternalSignaturesComplete,
    setCreateErpCustomerOnInternalSignaturesComplete,
  ] = useState(
    supplier?.creditApplicationSetting?.createErpCustomerOnInternalSignaturesComplete ?? false
  );
  const [preserveExistingAccountDetails, setPreserveExistingAccountDetails] = useState(
    supplier?.creditApplicationSetting?.preserveExistingAccountDetails ?? false
  );
  const [skipCustomerActionOnCod, setSkipCustomerActionOnCod] = useState(
    supplier?.creditApplicationSetting?.skipCustomerActionOnCod ?? false
  );

  useEffect(() => {
    setCustomerOnApprovalAction(
      (supplier?.creditApplicationSetting?.customerOnApprovalAction as CustomerOnApprovalAction) ??
        'none'
    );
    setPreserveExistingAccountDetails(
      supplier?.creditApplicationSetting?.preserveExistingAccountDetails ?? false
    );
    setSkipCustomerActionOnCod(
      supplier?.creditApplicationSetting?.skipCustomerActionOnCod ?? false
    );
    setCreateErpCustomerOnInternalSignaturesComplete(
      supplier?.creditApplicationSetting?.createErpCustomerOnInternalSignaturesComplete ?? false
    );
  }, [supplier]);

  const integrationActionDirty =
    customerOnApprovalAction !==
      ((supplier?.creditApplicationSetting?.customerOnApprovalAction as CustomerOnApprovalAction) ??
        'none') ||
    preserveExistingAccountDetails !==
      (supplier?.creditApplicationSetting?.preserveExistingAccountDetails ?? false) ||
    skipCustomerActionOnCod !==
      (supplier?.creditApplicationSetting?.skipCustomerActionOnCod ?? false) ||
    createErpCustomerOnInternalSignaturesComplete !==
      (supplier?.creditApplicationSetting?.createErpCustomerOnInternalSignaturesComplete ?? false);

  const saveIntegrationAction = async () => {
    setSavingIntegrationAction(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: {
          creditApplicationSetting: {
            ...supplier?.creditApplicationSetting,
            customerOnApprovalAction,
            ...(isP21 && { preserveExistingAccountDetails }),
            skipCustomerActionOnCod,
            ...(isEclipse && { createErpCustomerOnInternalSignaturesComplete }),
          },
        },
      })
    );
    setSavingIntegrationAction(false);
    if (result.type.includes('fulfilled')) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Setting saved.' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save setting.' });
    }
  };

  const integrationName = getIntegrationName(supplier?.integrationType);

  const integrationActionOptions: {
    label: string;
    value: CustomerOnApprovalAction;
    description: string;
  }[] = [
    {
      value: 'none',
      label: 'No action',
      description: 'Do nothing in the integration on approval.',
    },
    {
      value: 'create',
      label: `Create customer in ${integrationName}`,
      description: `Automatically create a new customer in ${integrationName} when a credit application is approved.`,
    },
    {
      value: 'update',
      label: `Update customer in ${integrationName}`,
      description: `Update an existing customer in ${integrationName} on approval. You will be prompted for the customer number.`,
    },
  ];

  return (
    <>
      {/* filterEachReviewer + approval email recipients (one save) */}
      <SectionCard>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900 m-0">Sales Rep Filtering</p>
              <p className="text-xs text-gray-400 mt-0.5 m-0">
                Control which credit applications each sales rep can see.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200">
              <SettingToggleRow
                label="Filter by assigned sales rep"
                description="Sales reps only see the applications assigned to them"
                value={filterEachReviewer}
                onChange={setFilterEachReviewer}
                canEdit={canEdit}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900 m-0">Approval Email Recipients</p>
              <p className="text-xs text-gray-400 mt-0.5 m-0">
                Who else is pre-filled on the approval email. The applicant and your credit
                application emails are always included, and the list can still be edited when
                approving.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
              <SettingToggleRow
                label="Sales rep"
                description="The sales reps assigned to the application"
                value={notifySalesRepOnApproval}
                onChange={setNotifySalesRepOnApproval}
                canEdit={canEdit}
              />
              <SettingToggleRow
                label="Internal signature document signers"
                description="Everyone who signed the application's internal signature documents (e.g. the Branch Coversheet)"
                value={notifySignersOnApproval}
                onChange={setNotifySignersOnApproval}
                canEdit={canEdit}
              />
            </div>
          </div>

          <div
            className="flex items-center justify-end gap-3"
            data-tooltip-id={!canEdit ? 'no-permission-tooltip-toggle' : undefined}>
            {toggleDirty && <span className="text-[11px] text-gray-500">Unsaved changes</span>}
            <DillButton
              label="Save Settings"
              onClick={saveToggle}
              isLoading={savingToggle}
              isDisabled={!canEdit || !toggleDirty || savingToggle}
            />
          </div>
        </div>

        {!canEdit && (
          <ReactTooltip
            id="no-permission-tooltip-toggle"
            place="top"
            variant="dark"
            opacity="100%"
            style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
            You do not have permission to make changes
          </ReactTooltip>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 gap-4">
        {/* stale ball-in-court reminders */}
        <SectionCard>
          <div className="flex flex-col gap-5">
            <div>
              <p className="text-sm font-semibold text-gray-900 m-0">
                Stale Ball-In-Court Reminders
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Send the assigned reviewer/decision-maker a weekly email digest (every Monday) of
                credit applications currently sitting in their name.
              </p>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="text-xs font-semibold text-gray-700 m-0">Enable reminders</p>
                <p className="text-[11px] text-gray-400 mt-0.5 m-0">
                  Send a weekly summary email of ball-in-court applications
                </p>
              </div>
              <div data-tooltip-id={!canEdit ? 'no-permission-tooltip-stale-reminder' : undefined}>
                <DillToggle
                  value={isStaleReminderEnabled}
                  onChange={setIsStaleReminderEnabled}
                  isDisabled={!canEdit}
                />
              </div>
            </div>

            <div
              className="flex justify-end pt-1"
              data-tooltip-id={!canEdit ? 'no-permission-tooltip-stale-reminder' : undefined}>
              <DillButton
                label="Save Settings"
                onClick={saveStaleReminder}
                isLoading={savingStaleReminder}
                isDisabled={!canEdit || !staleReminderDirty || savingStaleReminder}
              />
            </div>
          </div>

          {!canEdit && (
            <ReactTooltip
              id="no-permission-tooltip-stale-reminder"
              place="top"
              variant="dark"
              opacity="100%"
              style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
              You do not have permission to make changes
            </ReactTooltip>
          )}
        </SectionCard>
      </div>

      {/* creditApplicationEmails / internalEmails */}
      <SectionCard>
        <div className="flex flex-col gap-5">
          <div>
            <p className="text-sm font-semibold text-gray-900 m-0">Credit Application Emails</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Email addresses notified about credit applications. Any email address works.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <EmailListEditor
              supplier={supplier}
              canEdit={canEdit}
              field="creditApplicationEmails"
              title="New application notifications"
              description="Notified when a new credit application is submitted."
            />
            <div className="border-t border-gray-200 pt-5 md:border-t-0 md:pt-0 md:border-l md:pl-5">
              <EmailListEditor
                supplier={supplier}
                canEdit={canEdit}
                field="internalEmails"
                title="Internal approval recipients"
                description="Added by default when a credit application is internally approved."
              />
            </div>
          </div>
        </div>

        {!canEdit && (
          <ReactTooltip
            id="no-permission-tooltip-emails"
            place="top"
            variant="dark"
            opacity="100%"
            style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
            You do not have permission to make changes
          </ReactTooltip>
        )}
      </SectionCard>

      <BlockedTradeReferenceDomainsCard supplier={supplier} canEdit={canEdit} />

      {isERPCreditSetup && (
        <SectionCard>
          <div className="flex flex-col gap-5">
            <div>
              <p className="text-sm font-semibold text-gray-900 m-0">
                {integrationName} Customer Action
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Choose what happens in {integrationName} when a credit application is approved.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              {integrationActionOptions.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    customerOnApprovalAction === opt.value
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  } ${!canEdit ? 'opacity-50 pointer-events-none' : ''}`}>
                  <input
                    type="radio"
                    name="customerOnApprovalAction"
                    value={opt.value}
                    checked={customerOnApprovalAction === opt.value}
                    onChange={() => setCustomerOnApprovalAction(opt.value)}
                    disabled={!canEdit}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div>
                    <p className="text-xs font-semibold text-gray-800 m-0">{opt.label}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5 m-0">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>

            {isP21 && (
              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="text-xs font-semibold text-gray-700 m-0">
                    Preserve existing account details
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5 m-0">
                    When updating an existing P21 customer, keep their original account-opened date
                    instead of overwriting it with today&apos;s date.
                  </p>
                </div>
                <div data-tooltip-id={!canEdit ? 'no-permission-tooltip-toggle' : undefined}>
                  <DillToggle
                    value={preserveExistingAccountDetails}
                    onChange={setPreserveExistingAccountDetails}
                    isDisabled={!canEdit}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <p className="text-xs font-semibold text-gray-700 m-0">
                  Skip customer account on COD approval
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5 m-0">
                  When approving as COD, never create or update a customer in {integrationName} and
                  never ask for a customer number. Full credit approvals are unaffected.
                </p>
              </div>
              <div data-tooltip-id={!canEdit ? 'no-permission-tooltip-toggle' : undefined}>
                <DillToggle
                  value={skipCustomerActionOnCod}
                  onChange={setSkipCustomerActionOnCod}
                  isDisabled={!canEdit}
                />
              </div>
            </div>

            {isEclipse && (
              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="text-xs font-semibold text-gray-700 m-0">
                    Create customer when internal signatures are complete
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5 m-0">
                    Once every internal signature (e.g. the branch coversheet) is collected, create
                    the Bill-To and a Ship-To on COD terms in {integrationName} so sales can order
                    right away. Approving then only sets the Ship-To&apos;s terms and credit limit.
                  </p>
                </div>
                <div data-tooltip-id={!canEdit ? 'no-permission-tooltip-toggle' : undefined}>
                  <DillToggle
                    value={createErpCustomerOnInternalSignaturesComplete}
                    onChange={setCreateErpCustomerOnInternalSignaturesComplete}
                    isDisabled={!canEdit}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <DillButton
                label="Save Settings"
                onClick={saveIntegrationAction}
                isLoading={savingIntegrationAction}
                isDisabled={!canEdit || !integrationActionDirty || savingIntegrationAction}
              />
            </div>
          </div>
        </SectionCard>
      )}
    </>
  );
}
